import { BooleanInput, Container, Label, NumericInput, SliderInput, VectorInput } from '@playcanvas/pcui';

import { Events } from '../events';
import { i18n } from './localization';
import cameraSettingsSvg from './svg/camera-settings.svg';

const createSvg = (svgString: string) => {
    const decodedStr = decodeURIComponent(svgString.substring('data:image/svg+xml,'.length));
    return new DOMParser().parseFromString(decodedStr, 'image/svg+xml').documentElement;
};

class CameraSettingsPanel extends Container {
    constructor(events: Events, args = {}) {
        args = {
            ...args,
            id: 'camera-settings-panel',
            class: 'panel',
            hidden: true
        };

        super(args);

        // stop pointer events bubbling
        ['pointerdown', 'pointerup', 'pointermove', 'wheel', 'dblclick'].forEach((eventName) => {
            this.dom.addEventListener(eventName, (event: Event) => event.stopPropagation());
        });

        const header = new Container({ class: 'panel-header' });
        const headerIcon = new Container({ class: 'panel-header-icon' });
        headerIcon.dom.appendChild(createSvg(cameraSettingsSvg));
        const headerLabel = new Label({ class: 'panel-header-label' });
        i18n.bindText(headerLabel, 'panel.camera-settings');
        header.append(headerIcon);
        header.append(headerLabel);

        const showCameraPosesRow = new Container({ class: 'settings-panel-row' });
        const showCameraPosesLabel = new Label({ class: 'settings-panel-row-label' });
        i18n.bindText(showCameraPosesLabel, 'panel.camera-settings.show-cameras');
        const showCameraPosesToggle = new BooleanInput({
            type: 'toggle',
            class: 'settings-panel-row-toggle',
            value: events.invokeOptional('camera.showPoses') ?? false
        });
        showCameraPosesRow.append(showCameraPosesLabel);
        showCameraPosesRow.append(showCameraPosesToggle);

        const poseTransform = events.invoke('camera.poseTransform') ?? {
            position: [0, 0, 0],
            rotation: [0, 0, 0],
            scale: 1
        };

        const createTransformVectorRow = (key: string, precision: number, value: [number, number, number]) => {
            const row = new Container({ class: ['settings-panel-row', 'camera-settings-transform-row'] });
            const label = new Label({ class: 'settings-panel-row-label' });
            i18n.bindText(label, key);
            const input = new VectorInput({
                class: 'settings-panel-row-vector',
                precision,
                dimensions: 3,
                placeholder: ['X', 'Y', 'Z'],
                value
            });
            row.append(label);
            row.append(input);
            return { row, input };
        };

        const position = createTransformVectorRow('panel.camera-settings.transform.position', 3, poseTransform.position);
        const rotation = createTransformVectorRow('panel.camera-settings.transform.rotation', 2, poseTransform.rotation);

        const scaleRow = new Container({ class: ['settings-panel-row', 'camera-settings-transform-row'] });
        const scaleLabel = new Label({ class: 'settings-panel-row-label' });
        i18n.bindText(scaleLabel, 'panel.camera-settings.transform.scale');
        const scaleInput = new NumericInput({
            class: 'settings-panel-row-number',
            precision: 3,
            min: 0.001,
            max: 10000,
            value: poseTransform.scale
        });
        scaleRow.append(scaleLabel);
        scaleRow.append(scaleInput);

        const gizmoScaleRow = new Container({ class: 'settings-panel-row' });
        const gizmoScaleLabel = new Label({ class: 'settings-panel-row-label' });
        i18n.bindText(gizmoScaleLabel, 'panel.camera-settings.gizmo-scale');
        const gizmoScaleSlider = new SliderInput({
            class: 'settings-panel-row-slider',
            min: 0.1,
            max: 10,
            precision: 1,
            value: events.invoke('camera.poseGizmoScale') ?? 1
        });
        gizmoScaleRow.append(gizmoScaleLabel);
        gizmoScaleRow.append(gizmoScaleSlider);

        const hint = new Label({ class: 'panel-data-hint' });
        i18n.bindText(hint, 'panel.camera-settings.missing-data');

        this.append(header);
        this.append(hint);
        this.append(showCameraPosesRow);
        this.append(position.row);
        this.append(rotation.row);
        this.append(scaleRow);
        this.append(gizmoScaleRow);

        let suppress = false;

        const syncCameraToggles = () => {
            showCameraPosesToggle.value = events.invoke('camera.showPoses') ?? false;
        };

        const updatePoseTransform = () => {
            if (suppress) return;
            events.fire('camera.setPoseTransform', {
                position: position.input.value,
                rotation: rotation.input.value,
                scale: scaleInput.value
            });
        };

        events.on('camera.showPoses', (visible: boolean) => {
            showCameraPosesToggle.value = visible;
        });
        showCameraPosesToggle.on('change', (visible: boolean) => {
            events.fire('camera.setShowPoses', visible);
        });

        events.on('camera.poseTransform', (transform: {
            position: [number, number, number],
            rotation: [number, number, number],
            scale: number
        }) => {
            suppress = true;
            position.input.value = transform.position;
            rotation.input.value = transform.rotation;
            scaleInput.value = transform.scale;
            suppress = false;
        });
        [position.input.inputs, rotation.input.inputs, scaleInput].flat().forEach(input => input.on('change', updatePoseTransform));

        events.on('camera.poseGizmoScale', (value: number) => {
            gizmoScaleSlider.value = value;
        });
        gizmoScaleSlider.on('change', (value: number) => {
            events.fire('camera.setPoseGizmoScale', value);
        });

        const setVisible = (visible: boolean) => {
            if (visible === this.hidden) {
                if (visible) {
                    events.fire('settingsPanel.setVisible', false);
                    events.fire('appearancePanel.setVisible', false);
                    events.fire('overlaysPanel.setVisible', false);
                    syncCameraToggles();
                }
                this.hidden = !visible;
                events.fire('cameraSettingsPanel.visible', visible);
            }
        };

        events.function('cameraSettingsPanel.visible', () => {
            return !this.hidden;
        });

        events.on('cameraSettingsPanel.setVisible', (visible: boolean) => {
            setVisible(visible);
        });

        events.on('cameraSettingsPanel.toggleVisible', () => {
            setVisible(this.hidden);
        });

        events.on('settingsPanel.visible', (visible: boolean) => {
            if (visible) {
                setVisible(false);
            }
        });

        events.on('appearancePanel.visible', (visible: boolean) => {
            if (visible) setVisible(false);
        });
        events.on('overlaysPanel.visible', (visible: boolean) => {
            if (visible) {
                setVisible(false);
            }
        });

        const updateAvailability = () => {
            const available = (events.invoke('camera.poses')?.length ?? 0) > 0;
            hint.hidden = available;
            [showCameraPosesRow, position.row, rotation.row, scaleRow, gizmoScaleRow].forEach((row) => {
                row.hidden = !available;
            });
        };
        events.on('camera.posesChanged', updateAvailability);
        updateAvailability();
    }
}

export { CameraSettingsPanel };
